"use strict";

// ============================================================================
// Devin Web Gateway — chat app page
// Layout: sidebar (history) + main (onboarding → chat)
// Requires authentication (shared.js handles token + redirect)
// ============================================================================

import { $,
  sidebar, sbList, sbNew, sbFooter,
  onboarding, chatView, dirDisplay, dirPath, onboardStart, recentDirs,
  sessionSub, dirBadge, toggleSidebarBtn, sidebarOverlay, typingIndicator,
  attachBtn, fileInput, sbFilesBtn,
  fvOverlay,
  messagesEl, inner, input, sendBtn, stopBtn, composerHint, pastePreview,
  composerInput, queuedTray, reconnectBanner, reconnectBtn,
  sbShellsBtn, sbShellsBadge, sbStatsBtn, statsOverlay, statsClose, statsContent,
  sbSessionsBtn, sbSessionsBadge, sessionsOverlay, sessionsClose, sessionsContent,
  terminalOverlay, terminalTabs, terminalNew, terminalClose, terminalContainer,
  modelPickerBtn, mpOverlay, mpClose, mpCurrent, mpSearch, mpList,
  modeBadge, modeMenu, modeMenuList, modeOverlay,
} from "./dom.js";
import {
  loadRecentDirs, saveRecentDir,
  saveActiveSession, loadActiveSession, clearActiveSession,
  clearFvStateMap as clearFvState,
} from "./persistence.js";
import {
  isTableSep, renderTable, renderMarkdown,
  expandRefTags, inlineMd,
} from "./markdown.js";
import { on, off, emit } from "./events.js";
import {
  openMsgDB, nodeKey, cacheNode, evictLRU,
  restoreCachedNodes, clearCachedSession, NODE_CACHE_CAP,
} from "./cache.js";
import { wireModal, openModal, closeModal, isModalOpen, registerEscModal, initEscHandler } from "./modal.js";
import { startStatsPolling } from "./stats.js";
import { state } from "./state.js";
import { initTabTitle, updateTabTitle, tabAnnounce, tabBasename, tabCurrentName, setActiveCwd as _setActiveCwd } from "./tabtitle.js";
import { bumpUnread, maybeNotify, notifTitle, ensurePushSubscription, initNotifications } from "./notifications.js";
import {
  loadModels, findModelCost, findFamilyBySlug, setConfigOptions,
  restoreSavedModel, renderModelPicker, toggleModelDropdown,
  closeModelDropdown, initModelPicker,
} from "./model-picker.js";
import {
  onShellCreated, onShellOutput, onShellExit, onShellList,
  openTerminalModal, closeTerminalModal, initShells,
} from "./shells.js";
import { updateSessionsBadge, renderSessionsModal, initSessionsModal } from "./sessions-modal.js";
import { onSessionChanged as pinsSessionChanged } from "./pins.js";
import { toggleModeMenu, closeModeMenu, isModeMenuOpen, renderModeMenu, initModePicker } from "./mode-picker.js";
import { initSlashCommandPicker, openCommandPicker, closeCommandPicker, executeSelectedCommand, navigateCommandPicker, isCommandPickerOpen } from "./slash-command-picker.js";
import { initFileMentionPicker, updateFileMention, closeFileMention, executeSelectedFileMention, navigateFileMention, isFileMentionOpen } from "./file-mention-picker.js";
import { initFileViewer, wireFileLinks, openFileViewer, closeFileViewer, loadFvDir, restoreFvState, flushFvStateForCwdChange, closeFvTab, isFvPickerOpen, renderFvPickRecents } from "./file-viewer.js";
import {
  initMessageRendering, renderState,
  addEl, sysMsg, showReplayLoader, hideReplayLoader, updateReplayLoader,
  userMsg, rememberLiveUserMsg, handleUpdate,
  renderPermission, dismissPermissionCard,
  restoreCtxState, resetContextMeter,
} from "./message-rendering.js";
import {
  initWebSocket, send, connect, hideReconnectBanner, resetReconnectState,
} from "./websocket-transport.js";
import {
  showOnboarding, showChatView, setStatus, setBusy, setTyping,
  scrollDown, renderQueuedTray, initViewManager,
} from "./view-manager.js";
import {
  renderSidebar, deleteSession, loadSessionFromHistory, initSidebar,
} from "./sidebar.js";
import {
  selectDir, doStartSession, startSession, sendPrompt, newSession, changeFolder,
  modeShortLabel, resolveModeCommand, MODE_COMMAND_ALIASES, MODE_SHORT_LABELS,
  initSessionControl,
} from "./session-control.js";
import { openSidebar, closeSidebar, renderPastePreview, initEvents } from "./events-wiring.js";

// ---- state ----
let ws = null;
let sessionActive = false;
let busy = false;
let selectedDir = "";     // chosen in the folder picker (onboarding)
// activeCwd is managed via state.js (shared with tabtitle.js)
let activeCwd = "";       // cwd of the currently running session
// Wrapper syncs local activeCwd with state.activeCwd and handles cwd-change side effects.
function setActiveCwd(cwd, displayName) {
  _setActiveCwd(cwd, displayName, (prevCwd, newCwd) => {
    activeCwd = state.activeCwd;
    if (prevCwd !== newCwd) {
      if (prevCwd) flushFvStateForCwdChange(newCwd);
      resetContextMeter();
      restoreCtxState(newCwd);
    }
  });
  activeCwd = state.activeCwd;
}
let pendingStart = null;  // queued start message while WS connects
// Session to open once a sessions_list containing it arrives (?session=
// deep link from a push-notification cold start; the target's cwd isn't
// known until the list loads).
let pendingDeepLinkSession = null;
let currentSessionId = null;
// Sync state.currentSessionId with local currentSessionId (shared with notifications.js)
function setSessionId(id) {
  currentSessionId = id;
  state.currentSessionId = id;
  pinsSessionChanged(id); // re-key the pin strip to this session's working set
}
let expectedSessionId = null; // sessionId we asked to resume — used to detect fallback to new session
let resuming = false;     // true while attempting to resume a saved session (before "ready")
let sessions = []; // history from session/list
let liveSessionIds = new Set(); // session IDs with a live devin acp process in the pool
let liveCwds = new Set();       // cwds that have at least one live session
const deletedSessionIds = new Set(); // sessions pending deletion on the server
// modelFamilies, configOptions, modelConfigId are managed by model-picker.js
// Keep local aliases for read access; sync via setConfigOptions() on write.
let modelFamilies = []; // from /api/models
let configOptions = []; // from session/new response
let modelConfigId = null; // configId for the model selector
let pendingImages = []; // images to send with the next prompt
let availableCommands = []; // slash commands from available_commands_update
let availableModes = []; // session modes from initialize response
let currentModeId = null; // current session mode
let turboEnabled = false; // gateway-level turbo mode (auto-continue chains)
let queuedPromptCount = 0; // number of prompts queued while busy (back-compat, derived from queueState)
// Authoritative prompt-queue state from the server. Populated ONLY from
// `queue_update` messages — no shadow array. The server is the single
// source of truth; the UI is a pure projection.
//   running: { id, text, images, enqueuedAt } | null
//   queued:  Array<{ id, text, images, enqueuedAt }>  (index 0 = next to run)
let queueState = { running: null, queued: [] };

// ---- reconnect state ----
// reconnectTimer, reconnectAttempts, MAX_RECONNECT_DELAY, and connected are
// now internal to websocket-transport.js.
let intentionalClose = false; // true when user starts a new session / changes folder

// ---- message cache (IndexedDB, per-node LRU) — DB ops extracted to cache.js ----
// Stores rendered HTML per (sessionId, nodeId) so the UI can restore
// instantly on reload, before the WS reconnects and replays history.
// The server's sessions.db remains the source of truth; this cache only
// provides a fast partial preview on session open.
//
// In-memory mirrors for the current session remain here (they're session state).
let nodeOrderMap = new Map();   // nodeId -> order
let sessionMinOrder = 0;
let sessionMaxOrder = -1;

// cacheNode wrapper — passes the in-memory orderMap to the cache module.
async function cacheNodeLocal(sessionId, nodeId, html, order) {
  await cacheNode(sessionId, nodeId, html, order, nodeOrderMap);
  scheduleEvictLocal(sessionId);
}

let evictTimer = null;
let evictSessionId = null;
function scheduleEvictLocal(sessionId) {
  evictSessionId = sessionId;
  if (evictTimer) return;
  evictTimer = setTimeout(() => { evictTimer = null; void evictLRU(evictSessionId); }, 1000);
}

// Cache a batch of DOM nodes rendered for one page. `mode` is "prepend"
// (older history inserted at the top) or "append" (newer history / live
// updates at the bottom). Orders are assigned so an oldest→newest restore
// is possible; nodes are sorted into DOM order (top→bottom = oldest→newest)
// before assignment.
async function cachePageNodes(nodes, mode) {
  if (!currentSessionId || nodes.length === 0) return;
  const ordered = nodes.slice().sort((a, b) => {
    if (a === b) return 0;
    const rel = a.compareDocumentPosition(b);
    if (rel & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
    if (rel & Node.DOCUMENT_POSITION_PRECEDING) return 1;
    return 0;
  });
  const n = ordered.length;
  const sid = currentSessionId;
  if (mode === "prepend") {
    for (let i = 0; i < n; i++) {
      void cacheNodeLocal(sid, ordered[i].dataset.nodeId, ordered[i].outerHTML, sessionMinOrder - n + i);
    }
    sessionMinOrder -= n;
  } else {
    for (let i = 0; i < n; i++) {
      void cacheNodeLocal(sid, ordered[i].dataset.nodeId, ordered[i].outerHTML, sessionMaxOrder + 1 + i);
    }
    sessionMaxOrder += n;
  }
}

// restoreCachedNodes and clearCachedSession are imported from cache.js.

// Reset per-session in-memory cache state (on session switch / replay start).
function resetNodeCacheState() {
  nodeOrderMap = new Map();
  sessionMinOrder = 0;
  sessionMaxOrder = -1;
}

// clearCachedSession is imported from cache.js.

// Debounced cache write — saves the currently-visible DOM nodes per-node
// during streaming so a disconnect mid-turn doesn't lose content. Existing
// nodes are re-cached with their latest (now-complete) HTML; new nodes get
// appended orders.
let cacheTimer = null;
function debouncedCacheMessages() {
  if (cacheTimer) clearTimeout(cacheTimer);
  cacheTimer = setTimeout(() => { cacheTimer = null; cacheVisibleNodes(); }, 2000);
}
function cacheVisibleNodes() {
  if (!currentSessionId) return;
  const sid = currentSessionId;
  const newNodes = [];
  for (const child of inner.children) {
    const nid = child.dataset.nodeId;
    if (!nid) continue;
    const order = nodeOrderMap.get(nid);
    if (order !== undefined) {
      void cacheNodeLocal(sid, nid, child.outerHTML, order);
    } else {
      newNodes.push(child);
    }
  }
  if (newNodes.length > 0) void cachePageNodes(newNodes, "append");
}

// Cap DOM nodes to keep memory flat in very long conversations. We evict
// whole pages from the TOP (oldest history) — the only direction that is
// refillable via history_fetch scroll-back. Each page's nodes share a
// dataset.cursor (the page's oldest-row cursor); evicting a full page and
// pointing replayOldestCursor at the new top makes scroll-back gap-free.
// Bottom (newest) nodes are never evicted because there is no fetch-newer
// mechanism — they're kept, which is correct since the user lives at the
// bottom and live updates append there.
const MAX_DOM_MESSAGES = 500;
function trimDomMessages() {
  let evicted = false;
  while (inner.children.length > MAX_DOM_MESSAGES) {
    const top = inner.children[0];
    if (!top) break;
    const topCursor = top.dataset.cursor || null;
    if (!topCursor) {
      // Live/sys node with no scroll-back cursor — evict it alone.
      inner.removeChild(top);
      evicted = true;
      continue;
    }
    // Evict the whole page: all contiguous top nodes sharing topCursor.
    while (inner.children[0] && inner.children[0].dataset.cursor === topCursor) {
      inner.removeChild(inner.children[0]);
    }
    evicted = true;
  }
  // Only update the scroll-back cursor if we actually evicted something —
  // otherwise leave replayOldestCursor as the page handler set it (e.g.
  // null when the replay reached the beginning of the session).
  if (evicted) {
    // Find the first remaining node that carries a page cursor — that's the
    // oldest rendered page and bounds the next scroll-back fetch. A
    // no-cursor top node (live/sys) doesn't move the boundary; crucially,
    // do NOT fall back to null — that permanently kills scroll-back.
    for (const child of inner.children) {
      if (child.dataset.cursor) {
        replayOldestCursor = child.dataset.cursor;
        break;
      }
    }
  }
}

const agentMsgs = new Map();
const toolCards = new Map();
let loadingHistory = false; // true while replaying session history (to replace cached content)
// Monotonic token used to invalidate stale cache-restore promises. Each
// call to loadSessionFromHistory increments this; the restore callback
// skips injection if the token has since changed (e.g. the user switched
// sessions again, or a replay already started and cleared the DOM).
let cacheRestoreToken = 0;

// ---- Paginated history replay state ----
// The server sends history in pages (most-recent first) with ACK-based
// flow control. We render each page as it arrives, ACK it, and the
// server sends the next. A loader indicator shows progress.
let replayingHistory = false;       // true during a paginated replay
let replayTotal = 0;                // total updates expected (from start msg)
let replayReceived = 0;             // updates received so far
let replayPageSize = 50;            // page size (from start msg)
let replayOldestCursor = null;      // cursor of the oldest page loaded (for scroll-back)
let replayLoaderEl = null;          // DOM element for the loading indicator
let replayFirstPageRendered = false; // scroll to bottom after first page only

// ============================================================================
// Persistence — extracted to persistence.js
// (removeRecentDir, recomputeLiveCwds, fetchLiveCwds, renderRecentDirs, saveFvState
//  remain here because they reference session state and DOM elements)
// ============================================================================
function removeRecentDir(dir) {
  const dirs = loadRecentDirs().filter((d) => d !== dir);
  localStorage.setItem("devin-recent-dirs", JSON.stringify(dirs));
  clearFvState(dir);
  renderRecentDirs();
  if (isFvPickerOpen()) renderFvPickRecents();
}
/**
 * Recompute `liveCwds` from the in-memory `sessions` list + `liveSessionIds`
 * set. Called whenever sessions_list arrives. Also refreshes the onboarding
 * recent chips so the live dot stays in sync.
 */
function recomputeLiveCwds() {
  const next = new Set();
  for (const s of sessions) {
    if (liveSessionIds.has(s.sessionId) && s.cwd) next.add(s.cwd);
  }
  // Also include the active session's cwd if we have one.
  if (currentSessionId && liveSessionIds.has(currentSessionId) && activeCwd) {
    next.add(activeCwd);
  }
  liveCwds = next;
  renderRecentDirs();
  // If the file viewer is open in pick intent, refresh its recents too.
  if (isFvPickerOpen()) renderFvPickRecents();
}
/**
 * Fetch live cwds from /api/live-cwds. Used on the onboarding screen before
 * any WS connection exists, so recent chips can show live dots there too.
 */
async function fetchLiveCwds() {
  try {
    const res = await fetch("/api/live-cwds", {
      headers: { "Authorization": "Bearer " + Token.get() },
    });
    if (!res.ok) return;
    const data = await res.json();
    liveCwds = new Set(Object.keys(data));
    renderRecentDirs();
    if (isFvPickerOpen()) renderFvPickRecents();
  } catch { /* ignore */ }
}
const ONBOARD_RECENT_DIRS_MAX = 3;
function renderRecentDirs() {
  const dirs = loadRecentDirs();
  recentDirs.innerHTML = "";
  for (const d of dirs.slice(0, ONBOARD_RECENT_DIRS_MAX)) {
    const chip = document.createElement("span");
    chip.className = "chip" + (liveCwds.has(d) ? " live" : "");
    chip.textContent = d;
    chip.title = liveCwds.has(d) ? d + " (live session)" : d;
    chip.onclick = () => { haptic("tap"); selectDir(d); };
    recentDirs.appendChild(chip);
  }
  const browseChip = document.createElement("span");
  browseChip.className = "chip chip-browse";
  browseChip.textContent = "📁 Choose folder…";
  browseChip.title = "Browse for a different folder";
  browseChip.onclick = () => { haptic("tap"); openFileViewer({ intent: "pick", mode: "onboard" }); };
  recentDirs.appendChild(browseChip);
}

// ---- session persistence (resume on reload) — extracted to persistence.js ----
// (file viewer state persistence — saveFvState, wireFileLinks — extracted to file-viewer.js)

// ============================================================================
// View management — extracted to view-manager.js
// State variables remain here (shared with other sections via deps).
// ============================================================================
let userPinnedAway = false;
let scrollTimer = null;
let scrollBackFetching = false;

// ============================================================================
// Sidebar (session history) — extracted to sidebar.js
// ============================================================================



// ============================================================================
// Session start / folder change — extracted to session-control.js
// Events — extracted to events-wiring.js
// ============================================================================

// Session control — handled by initSessionControl in session-control.js
initSessionControl({
  getSelectedDir: () => selectedDir,
  setSelectedDir: (v) => { selectedDir = v; },
  getWs: () => ws,
  getSessionActive: () => sessionActive,
  setSessionActive: (v) => { sessionActive = v; },
  getPendingStart: () => pendingStart,
  setPendingStart: (v) => { pendingStart = v; },
  getActiveCwd: () => activeCwd,
  getBusy: () => busy,
  getPendingImages: () => pendingImages,
  setPendingImages: (v) => { pendingImages = v; },
  getAvailableModes: () => availableModes,
  getTurboEnabled: () => turboEnabled,
  setQueueState: (v) => { queueState = v; },
  setIntentionalClose: (v) => { intentionalClose = v; },
  setLoadingHistory: (v) => { loadingHistory = v; },
  setResuming: (v) => { resuming = v; },
  setExpectedSessionId: (v) => { expectedSessionId = v; },
  setSessions: (v) => { sessions = v; },
  setUserPinnedAway: (v) => { userPinnedAway = v; },
  getAgentMsgs: () => agentMsgs,
  getToolCards: () => toolCards,
  send,
  setActiveCwd,
  showChatView,
  setStatus,
  renderSidebar,
  setSessionId,
  connect,
  saveRecentDir,
  renderQueuedTray,
  hideReconnectBanner,
  userMsg,
  rememberLiveUserMsg,
  renderPastePreview,
  setBusy,
});

// Events — handled by initEvents in events-wiring.js
initEvents({
  // State — session
  getSelectedDir: () => selectedDir,
  setSelectedDir: (v) => { selectedDir = v; },
  getWs: () => ws,
  setWs: (v) => { ws = v; },
  getSessionActive: () => sessionActive,
  setSessionActive: (v) => { sessionActive = v; },
  getPendingStart: () => pendingStart,
  setPendingStart: (v) => { pendingStart = v; },
  getResuming: () => resuming,
  setResuming: (v) => { resuming = v; },
  getIntentionalClose: () => intentionalClose,
  setIntentionalClose: (v) => { intentionalClose = v; },
  getCurrentSessionId: () => currentSessionId,
  getActiveCwd: () => activeCwd,
  getExpectedSessionId: () => expectedSessionId,
  setExpectedSessionId: (v) => { expectedSessionId = v; },
  getPendingDeepLinkSession: () => pendingDeepLinkSession,
  setPendingDeepLinkSession: (v) => { pendingDeepLinkSession = v; },
  getSessions: () => sessions,
  setSessions: (v) => { sessions = v; },
  getLiveSessionIds: () => liveSessionIds,
  setLiveSessionIds: (v) => { liveSessionIds = v; },
  getDeletedSessionIds: () => deletedSessionIds,
  getConfigOptions: () => configOptions,
  setConfigOptions: (v) => { configOptions = v; },
  getAvailableModes: () => availableModes,
  setAvailableModes: (v) => { availableModes = v; },
  getCurrentModeId: () => currentModeId,
  setCurrentModeId: (v) => { currentModeId = v; },
  getTurboEnabled: () => turboEnabled,
  setTurboEnabled: (v) => { turboEnabled = v; },
  getLoadingHistory: () => loadingHistory,
  setLoadingHistory: (v) => { loadingHistory = v; },
  getReplayingHistory: () => replayingHistory,
  setReplayingHistory: (v) => { replayingHistory = v; },
  getReplayTotal: () => replayTotal,
  setReplayTotal: (v) => { replayTotal = v; },
  getReplayReceived: () => replayReceived,
  setReplayReceived: (v) => { replayReceived = v; },
  getReplayPageSize: () => replayPageSize,
  setReplayPageSize: (v) => { replayPageSize = v; },
  getReplayOldestCursor: () => replayOldestCursor,
  setReplayOldestCursor: (v) => { replayOldestCursor = v; },
  getReplayFirstPageRendered: () => replayFirstPageRendered,
  setReplayFirstPageRendered: (v) => { replayFirstPageRendered = v; },
  getReplayLoaderEl: () => replayLoaderEl,
  setReplayLoaderEl: (v) => { replayLoaderEl = v; },
  getScrollBackFetching: () => scrollBackFetching,
  setScrollBackFetching: (v) => { scrollBackFetching = v; },
  getQueuedPromptCount: () => queuedPromptCount,
  setQueuedPromptCount: (v) => { queuedPromptCount = v; },
  getQueueState: () => queueState,
  setQueueState: (v) => { queueState = v; },
  getCacheTimer: () => cacheTimer,
  setCacheTimer: (v) => { cacheTimer = v; },
  getAgentMsgs: () => agentMsgs,
  getToolCards: () => toolCards,
  getMaxDomMessages: () => MAX_DOM_MESSAGES,
  getUserPinnedAway: () => userPinnedAway,
  setUserPinnedAway: (v) => { userPinnedAway = v; },
  getScrollTimer: () => scrollTimer,
  setScrollTimer: (v) => { scrollTimer = v; },
  getCacheRestoreToken: () => cacheRestoreToken,
  incrementCacheRestoreToken: () => { cacheRestoreToken++; },
  getPendingImages: () => pendingImages,
  setPendingImages: (v) => { pendingImages = v; },
  getLiveCwds: () => liveCwds,
  getAvailableCommands: () => availableCommands,
  setAvailableCommands: (v) => { availableCommands = v; },
  getBusy: () => busy,
  setBusyState: (v) => { busy = v; },
  // Functions from main.js
  setActiveCwd,
  setSessionId,
  renderRecentDirs,
  removeRecentDir,
  fetchLiveCwds,
  recomputeLiveCwds,
  loadSessionFromHistory,
  resetNodeCacheState,
  trimDomMessages,
  debouncedCacheMessages,
  cachePageNodes,
  cacheVisibleNodes,
});

// ============================================================================
// Browser tab title state — extracted to tabtitle.js
// initTabTitle() must be called before the init block below, which calls
// setActiveCwd() → tabAnnounce()/updateTabTitle().
initTabTitle();
initNotifications(loadSessionFromHistory);

// ============================================================================
// Init — resume saved session instantly, verify auth in background
// ============================================================================
// Detect PWA standalone mode (installed app)
const isStandalone = window.matchMedia("(display-mode: standalone)").matches
  || window.navigator.standalone === true;

// Check token locally (instant) — don't block UI on a network auth round-trip.
// The WebSocket authenticates itself via ?token=, and API calls return 401 if
// the token is invalid. We only need requireAuth() for the user menu and to
// redirect to login if the token has expired.
const _token = Token.get();
if (!_token) {
  // No token at all — go straight to login.
  location.href = "/login.html";
} else {
  // Token exists — optimistically resume the saved session immediately,
  // then verify auth in the background (for user menu + login redirect).
  renderRecentDirs();
  fetchLiveCwds(); // mark recent folders that have live sessions
  loadModels(); // pre-fetch model list for cost display

  // Deep link: ?session=<id> opens a specific conversation (cold start from
  // a push-notification tap — the SW opens /app.html?session=…). We can't
  // start it directly (its cwd isn't known yet), so we stash it and switch
  // as soon as a sessions_list arrives containing it.
  const _deepLink = new URLSearchParams(location.search).get("session");
  if (_deepLink) {
    pendingDeepLinkSession = _deepLink;
    history.replaceState(null, "", location.pathname); // don't re-trigger on refresh
  }

  const saved = loadActiveSession();
  if (saved && saved.cwd && saved.sessionId) {
    // Resume the last session — go straight to chat view, no onboarding.
    // Cached messages show instantly; WS reconnects in background.
    selectedDir = saved.cwd;
    setActiveCwd(saved.cwd);
    dirBadge.textContent = saved.cwd;
    dirBadge.title = saved.cwd;
    pendingStart = { cwd: saved.cwd, sessionId: saved.sessionId };
    resuming = true;
    showChatView();
    setStatus("", "resuming…");
    loadingHistory = true;
    // Show cached nodes instantly while WS connects & session loads.
    // The server will replay its own ordered history once connected.
    resetNodeCacheState();
    restoreCachedNodes(saved.sessionId).then((nodes) => {
      if (nodes.length > 0 && loadingHistory) {
        for (const n of nodes) {
          if (!n.html) continue;
          const wrap = document.createElement("div");
          wrap.innerHTML = n.html;
          const el = wrap.firstElementChild;
          if (el) {
            // Cached anchors carry stale wiring markers (data-wired + href)
            // whose listeners don't survive serialization — strip and
            // re-wire so restored citations are clickable. Same fix as
            // loadSessionFromHistory's restore path.
            el.querySelectorAll("a.file-link[data-wired]").forEach((a) => {
              a.removeAttribute("data-wired");
              a.removeAttribute("href");
            });
            wireFileLinks(el);
            inner.appendChild(el);
            if (el.classList.contains("tool-card") && el.dataset.id) {
              toolCards.set(el.dataset.id, el);
            }
          }
        }
        userPinnedAway = false;
        scrollDown(true);
      }
    });
    connect();
  } else {
    // No saved session — show onboarding (folder picker). Connect the WS
    // anyway: list_sessions is answered from the gateway DB without any
    // ACP process, so the sidebar populates immediately instead of
    // staying on "No conversations yet" until a session starts.
    showOnboarding();
    connect();
  }

  // Fetch user info in the background for the user menu.
  // Don't use requireAuth() — it redirects to login on ANY error (including
  // transient network issues), which would interrupt the session resume.
  // The WebSocket's 4001 close code is the real auth gate; this is just for UX.
  // When there's no saved session (no WS connection), a 401 response means the
  // token is truly expired — redirect to login in that case only.
  const _hasSavedSession = saved && saved.cwd && saved.sessionId;
  fetch("/api/auth/me", { headers: { Authorization: `Bearer ${_token}` } })
    .then((res) => {
      if (res.status === 401) {
        // Token is invalid/expired. If the WS is handling auth (saved session
        // case), the 4001 close handler will redirect. Otherwise, redirect now.
        if (!_hasSavedSession) {
          Token.clear();
          location.href = "/login.html";
        }
        return null;
      }
      return res.ok ? res.json() : null;
    })
    .then((data) => {
      if (data?.user) {
        currentUser = data.user;
        createUserMenu(sbFooter);
      }
    })
    .catch(() => {}); // network errors are transient — WS auth is authoritative
}


// Initialize shells module (handles terminal modal + ESC registration)
initShells({ ws: () => ws, activeCwd: () => activeCwd, connect: connect });

// Initialize sessions modal
initSessionsModal({
  liveSessionIds: () => liveSessionIds,
  sessions: () => sessions,
  currentSessionId: () => currentSessionId,
  send: send,
  loadSessionFromHistory: loadSessionFromHistory,
});

// ESC to close sessions or stats (terminal ESC handled by shells.js)
registerEscModal(sessionsOverlay);
registerEscModal(statsOverlay);
initEscHandler();

// Start stats polling on page load (low frequency, speeds up when modal open)
startStatsPolling({ statsOverlay, statsContent, sbStatsBtn, statsClose });
